import type { ColumnDataTypeCategory, DataColumn, RuleOperatorKey } from "../types";

type RuleOperatorValueMode = "multi" | "single" | "text" | "range" | "none";

// `label` is the control label - what a <select> or a token chip shows, in Title Case.
// `sentence` is the same operator written as it belongs inside a sentence, for the Rule
// Book narrative (logic/narrative.ts). They live in one table on purpose: an operator
// added here without a sentence form would otherwise read as "Is Any Of" mid-paragraph,
// and a separate prose table would drift from this one the first time a label changed.
const RULE_OPERATORS: { key: RuleOperatorKey; label: string; sentence: string; valueMode: RuleOperatorValueMode }[] = [
    { key: "in", label: "Is Any Of", sentence: "is one of", valueMode: "multi" },
    { key: "not_in", label: "Is None Of", sentence: "is not one of", valueMode: "multi" },
    { key: "eq", label: "Equals", sentence: "is", valueMode: "single" },
    { key: "ne", label: "Does Not Equal", sentence: "is not", valueMode: "single" },
    { key: "contains", label: "Contains Text", sentence: "contains", valueMode: "text" },
    { key: "not_contains", label: "Does Not Contain Text", sentence: "does not contain", valueMode: "text" },
    { key: "starts_with", label: "Starts With", sentence: "starts with", valueMode: "text" },
    { key: "not_starts_with", label: "Does Not Start With", sentence: "does not start with", valueMode: "text" },
    { key: "ends_with", label: "Ends With", sentence: "ends with", valueMode: "text" },
    { key: "not_ends_with", label: "Does Not End With", sentence: "does not end with", valueMode: "text" },
    { key: "gt", label: "Greater Than", sentence: "is more than", valueMode: "single" },
    { key: "gte", label: "Greater Than Or Equal To", sentence: "is at least", valueMode: "single" },
    { key: "lt", label: "Less Than", sentence: "is less than", valueMode: "single" },
    { key: "lte", label: "Less Than Or Equal To", sentence: "is at most", valueMode: "single" },
    { key: "between", label: "Between", sentence: "is between", valueMode: "range" },
    { key: "not_between", label: "Not Between", sentence: "is not between", valueMode: "range" },
    { key: "before", label: "Before", sentence: "is before", valueMode: "single" },
    { key: "after", label: "After", sentence: "is after", valueMode: "single" },
    { key: "empty", label: "Is Blank", sentence: "has no value", valueMode: "none" },
    { key: "not_empty", label: "Is Not Blank", sentence: "has a value", valueMode: "none" },
];

// --- Data-type-aware operators ----------------------------------------------------
// WHERE THE OPERATOR/DATA-TYPE MAPPING COMES FROM
//
// rc.RuleOperator has always carried this mapping, in its AppliesToText / AppliesToNumber
// / AppliesToDate columns. It is served by GET /ReportConfiguration/rule-operators and
// supplied by the facade as an explicit catalog, which is the source of truth whenever it is
// populated.
//
// FALLBACK_OPERATORS_BY_DATA_TYPE is only used before that call returns, or if it fails.
// It is a transcription of the twelve rows as they stand in Dev, plus the five added with
// data-type-aware filtering and the three negated forms added for nested criteria
// (not_starts_with / not_ends_with / not_between) - not an independent opinion.
//
// WHY THE NEGATED FORMS MATTER. The exclusion side of a metric negates its WHOLE tree,
// so "include A OR NOT (B AND C)" cannot be split across the two sides - they are
// AND-ed. De Morgan rewrites it inside the include tree instead (NOT (B AND C) is
// B' OR C'), but only if every operator has a negated partner. in/not_in, eq/ne,
// contains/not_contains, gt/lte, gte/lt and empty/not_empty already pair up; these
// three were the gap, so any such expression is now writable in one tree. Keeping the two in step matters:
// when this was a standalone constant it had already drifted, offering gt/lt on text
// columns (the table says numeric and date) and withholding in/not_in from date columns
// (the table allows them).
const FALLBACK_OPERATORS_BY_DATA_TYPE: Record<ColumnDataTypeCategory, RuleOperatorKey[]> = {
    text: ["in", "not_in", "eq", "ne", "contains", "not_contains", "starts_with", "not_starts_with", "ends_with", "not_ends_with", "empty", "not_empty"],
    numeric: ["in", "not_in", "eq", "ne", "gt", "gte", "lt", "lte", "between", "not_between", "empty", "not_empty"],
    date: ["in", "not_in", "eq", "ne", "gt", "lt", "before", "after", "between", "not_between", "empty", "not_empty"],
};

/** One row of rc.RuleOperator as served to the browser. */
export interface RuleOperatorCatalogEntry {
    operatorId: number;
    operatorCode: RuleOperatorKey;
    label: string;
    valueModeId: number;
    appliesToText: boolean;
    appliesToNumber: boolean;
    appliesToDate: boolean;
    sortOrder: number;
}

/** The operator codes valid for a data type, from the catalog when loaded. */
function operatorCodesForDataType(category: ColumnDataTypeCategory, catalog: RuleOperatorCatalogEntry[]): RuleOperatorKey[] {
    if (!catalog.length) return FALLBACK_OPERATORS_BY_DATA_TYPE[category];

    const applies = (row: RuleOperatorCatalogEntry) =>
        category === "text" ? row.appliesToText
        : category === "numeric" ? row.appliesToNumber
        : row.appliesToDate;

    const codes = catalog.filter(applies).map((row) => row.operatorCode);
    // A catalog that allows nothing for this type would leave the user unable to build any
    // condition, so treat it as unusable rather than as an empty answer.
    return codes.length ? codes : FALLBACK_OPERATORS_BY_DATA_TYPE[category];
}

/** The label to show for an operator: the catalog's, falling back to the built-in one. */
function ruleOperatorLabel(operator: RuleOperatorKey, catalog: RuleOperatorCatalogEntry[]) {
    return catalog.find((row) => row.operatorCode === operator)?.label
        || ruleOpDef(operator).label;
}

/**
 * The operator as it reads inside a sentence ("is one of", "has no value").
 *
 * Unlike ruleOperatorLabel this does NOT consult the rc.RuleOperator catalog: that table
 * supplies control labels, which are written for a dropdown and would put Title Case in
 * the middle of a paragraph. The sentence form stays with the built-in table so the
 * narrative reads consistently whatever the catalog says.
 */
function ruleOperatorSentence(operator: RuleOperatorKey) {
    return ruleOpDef(operator).sentence;
}

const NUMERIC_SQL_TYPES = ["int", "bigint", "smallint", "tinyint", "decimal", "numeric", "float", "real", "money", "smallmoney", "double", "number"];
const DATE_SQL_TYPES = ["date", "datetime", "datetime2", "smalldatetime", "datetimeoffset", "time", "timestamp"];

/**
 * The data type bucket for a column, in order of trustworthiness: the explicit
 * DataTypeCategory, then a match against the raw DataType, then ColumnRole (a timestamp
 * role is a date column even when both type fields are blank). Anything still unknown is
 * "text" - see the note on ColumnDataTypeCategory in types.ts for why that default matters.
 */
function columnDataTypeCategory(column?: DataColumn | null): ColumnDataTypeCategory {
    if (!column) return "text";

    const category = String(column.dataTypeCategory || "").trim().toLowerCase();
    if (category) {
        if (category.startsWith("num") || category === "int" || category === "integer" || category === "decimal" || category === "money") return "numeric";
        if (category.startsWith("date") || category.startsWith("time")) return "date";
        if (category.startsWith("text") || category.startsWith("str") || category.startsWith("char")) return "text";
    }

    const dataType = String(column.dataType || "").trim().toLowerCase().replace(/\(.*$/, "");
    if (dataType) {
        if (NUMERIC_SQL_TYPES.includes(dataType)) return "numeric";
        if (DATE_SQL_TYPES.includes(dataType)) return "date";
    }

    const role = String(column.role || "").trim().toLowerCase();
    if (role === "timestamp" || role === "time_stamp" || role === "time stamp") return "date";

    return "text";
}

/**
 * The operator definitions valid for one column, in the canonical RULE_OPERATORS order.
 *
 * Only the metric filters restrict by data type. The Categorization builder deliberately
 * keeps offering every operator on every column: its column pool is selected by ColumnRole
 * rather than by type, so a category column can perfectly well be numeric or date, and
 * narrowing that list would make already-saved rules impossible to recreate - and would
 * remove "Is Any Of", the default operator for a new condition, from those columns entirely.
 */
function operatorsForColumn(column: DataColumn | null | undefined, catalog: RuleOperatorCatalogEntry[]) {
    const allowed = operatorCodesForDataType(columnDataTypeCategory(column), catalog);
    return RULE_OPERATORS.filter((operator) => allowed.includes(operator.key));
}

function isOperatorAllowedForColumn(column: DataColumn | null | undefined, operator: RuleOperatorKey, catalog: RuleOperatorCatalogEntry[]) {
    return operatorCodesForDataType(columnDataTypeCategory(column), catalog).includes(operator);
}

/**
 * The operator a condition should hold after its column changes. Keeping the current one
 * whenever it is still legal means switching between two text columns never silently
 * rewrites the condition; only a genuinely invalid combination is corrected, falling back
 * to the first operator the new type offers.
 */
function operatorForColumnChange(column: DataColumn | null | undefined, current: RuleOperatorKey, catalog: RuleOperatorCatalogEntry[]): RuleOperatorKey {
    if (isOperatorAllowedForColumn(column, current, catalog)) return current;
    return operatorsForColumn(column, catalog)[0]?.key || "in";
}

function ruleOpDef(operator: RuleOperatorKey) {
    return RULE_OPERATORS.find((item) => item.key === operator) || RULE_OPERATORS[0];
}

export {
    RULE_OPERATORS,
    DATE_SQL_TYPES,
    operatorCodesForDataType,
    ruleOperatorLabel,
    ruleOperatorSentence,
    columnDataTypeCategory,
    operatorsForColumn,
    isOperatorAllowedForColumn,
    operatorForColumnChange,
    ruleOpDef,
};
